import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteButton,
  FileIcon,
  FileMeta,
  FileName,
  InfoBlock,
  ItemCard,
  Thumbnail,
  UploadingSpinner,
} from './styles';

/** 표시용 첨부 모델 — 백엔드 응답을 가공해 컴포넌트에 전달하는 정규화 타입 */
export type AttachmentDisplayItem = {
  fileId: string;
  /** 화면에 표시할 파일명 (확장자 포함) */
  name: string;
  /** bytes */
  size: number;
  /** ISO datetime string */
  uploadedAt: string;
  /** 소문자 확장자(점 제외). 예: 'pdf', 'jpg' */
  ext: string;
  /** 이미지인 경우 썸네일 URL. 비이미지면 미지정 */
  thumbnailUrl?: string;
  /** 원본 다운로드 URL. 클릭 시 사용 가능 */
  url?: string;
  /** 업로드 중 임시 항목(레거시 .attachment-item.uploading) — 미리보기 위 스피너, 삭제 숨김 */
  uploading?: boolean;
};

const IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp']);

/** 확장자 → 아이콘 파일명 매핑. reference_legacy_file_attachment.md 표 기준 */
const iconForExt = (ext: string): string => {
  if (IMG_EXTS.has(ext)) return '/icons/approval/attachment-img.svg';
  if (ext === 'doc' || ext === 'docx')
    return '/icons/approval/attachment-docx.svg';
  if (ext === 'pdf') return '/icons/approval/attachment-pdf.svg';
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv')
    return '/icons/approval/attachment-xlsx.svg';
  if (ext === 'ppt' || ext === 'pptx')
    return '/icons/approval/attachment-ppt.svg';
  if (ext === 'hwp' || ext === 'hwpx')
    return '/icons/approval/attachment-hwp.svg';
  if (ext === 'txt') return '/icons/approval/attachment-txt.svg';
  if (
    ext === 'zip' ||
    ext === 'rar' ||
    ext === '7z' ||
    ext === 'tar' ||
    ext === 'gz'
  )
    return '/icons/approval/attachment-zip.svg';
  return '/icons/approval/attachment-file.svg';
};

const formatSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};

/** YYYY-MM-DD HH:mm:ss — 스크린샷 레거시 표시 형식. 타임존 변환은 호출자 책임. */
const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

type Props = {
  item: AttachmentDisplayItem;
  mode: 'view' | 'edit';
  onDelete?: (fileId: string) => void;
  onPreview?: (item: AttachmentDisplayItem) => void;
};

const AttachmentItem = ({ item, mode, onDelete, onPreview }: Props) => {
  const { t } = useTranslation();
  const isImage = IMG_EXTS.has(item.ext);
  const clickable = !!onPreview;

  const handleCardClick = () => {
    if (onPreview) onPreview(item);
  };

  const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete?.(item.fileId);
  };

  return (
    <ItemCard
      clickable={clickable}
      onClick={clickable ? handleCardClick : undefined}
    >
      {mode === 'edit' && !item.uploading && (
        <DeleteButton
          type="button"
          aria-label={t('첨부 삭제')}
          onClick={handleDelete}
        >
          <img src="/icons/approval/close.svg" alt="" aria-hidden="true" />
        </DeleteButton>
      )}
      <Thumbnail className={item.uploading ? 'is-uploading' : undefined}>
        {isImage && item.thumbnailUrl ? (
          <img className="thumb-image" src={item.thumbnailUrl} alt="" />
        ) : (
          <FileIcon src={iconForExt(item.ext)} alt="" aria-hidden="true" />
        )}
        {item.uploading && <UploadingSpinner aria-hidden="true" />}
      </Thumbnail>
      <InfoBlock>
        <FileName as="span" title={item.name}>
          {item.name}
        </FileName>
        {item.uploading ? <FileMeta as="span">Uploading...</FileMeta> : null}
        {item.uploadedAt ? (
          <FileMeta as="span">{formatDateTime(item.uploadedAt)}</FileMeta>
        ) : null}
        {item.size ? (
          <FileMeta as="span">{formatSize(item.size)}</FileMeta>
        ) : null}
      </InfoBlock>
    </ItemCard>
  );
};

export default AttachmentItem;
